import { HiveInfoUnplottedSensorFragment } from '@/.generated/graphql';
import { useAppSelector } from '@/redux/hooks';
import { ESensorMode, TInDirection, TXYPoint, ESensorModel, ESensorPowerType } from '@/types';
import { datadogRum } from '@datadog/browser-rum';
import { createSlice } from '@reduxjs/toolkit';
import { omit, pickBy } from 'lodash';
import {
  calculateSensorCoverage,
  getRectRotation,
  pointsToRect,
  rectToPoints,
  itemToCoordinates,
  getFovForModel,
  extractSeqFromMac,
  rotatePointAroundOrigin,
} from '../../utils/utils';
import type {
  FloorPlan,
  Gateway3P,
  Hive,
  Room,
  Sensor,
  Sensor3P,
  Spot,
  Zone,
} from '../useGetState';
import type { SaveResult } from '../types';
import { SensorObject, HiveObject, RoomObject, FloorPlanObject } from '../../sdk';
import { generateId } from '@/utils/ids';
import { generateCopyName } from '@/utils/naming';
import { TRectCoords, TPolygonCoords } from '@/types';
import {
  getDefaultMirrorName,
  getDefaultCloneName,
  flipInDirection,
} from '../../utils/virtualSensorUtils';
import type { HeatmapStyle } from '../../Heatmaps/Heatmaps';

/** Helper function to ensure coordinates match the expected type */
function ensureCoordinateType<T extends TRectCoords | TPolygonCoords>(
  coordinates: TPolygonCoords,
  originalCoordinates: T,
): T {
  // If original is TRectCoords and we have exactly 4 points, return as TRectCoords
  if (
    Array.isArray(originalCoordinates) &&
    originalCoordinates.length === 4 &&
    coordinates.length === 4
  ) {
    return coordinates as T;
  }
  // Otherwise return as the polygon type
  return coordinates as T;
}

/** Extend floor plan with additional properties not currently saved on the backend. */
export type AugmentedFloorPlan = FloorPlan & {
  opacity: number;
  skipDarkModeInversion?: boolean;
};

export type CoreReducerState = {
  base: {
    sensors: Sensor[];
    rooms: Room[];
    zones: Zone[];
    hives: Hive[];
    unplotted: Hive[];
    floorplans: FloorPlan[];
    spots: Spot[];
    sensors3p: Sensor3P[];
    gateways3p: Gateway3P[];
  };
  modified: {
    sensors: Record<string, Partial<Sensor>>;
    rooms: Record<string, Partial<Room>>;
    zones: Record<string, Partial<Zone>>;
    hives: Record<string, Partial<Hive>>;
    floorplans: Record<string, Partial<AugmentedFloorPlan>>;
    spots: Record<string, Partial<Spot>>;
    sensors3p: Record<string, Partial<Sensor3P>>;
    gateways3p: Record<string, Partial<Gateway3P>>;
  };
  deleted: string[];
  archiveChanges: Record<string, 'ARCHIVED' | 'ACTIVE'>;
  added: {
    sensors: Sensor[];
    rooms: Room[];
    zones: Zone[];
    hives: Hive[];
    floorplans: AugmentedFloorPlan[];
    spots: Spot[];
    sensors3p: Sensor3P[];
    gateways3p: Gateway3P[];
  };
  selected: Array<string>;
  // The selectedHovered state is used when we want to show hover state of selected items but can't not trigger their mouse event
  selectedHovered: boolean;
  // The editing state tracks which items are in polygon edit mode (double-clicked)
  editing: Array<string>;
  errors: {
    [id: string]: string;
  };
  search: {
    text: string;
    hoverItemId: string | null;
  };
  layers: LayerReducerState;
  heatmapStyle: HeatmapStyle;
  initialized: boolean;
  highlightRelatedDevices: boolean;
  clipboard: {
    rooms: Room[];
    zones: Zone[];
    sensors: Sensor[];
    spots: Spot[];
    origin?: 'copy' | 'cut';
  } | null;
  currentFloorId: string | null;
};

export type LayerState = { visible: boolean; locked: boolean; subLayers?: string[] };
export type LayerReducerState = {
  sensors: LayerState;
  rooms: LayerState;
  zones: LayerState;
  hives: LayerState;
  floorplans: LayerState;
  spots: LayerState;
  heatmaps: LayerState;
  detections: LayerState;
  sensorModel: LayerState;
  hiveModel: LayerState;
  archivedDevices: LayerState;
  batteryStatus: LayerState;
  sensors3p: LayerState;
  gateways3p: LayerState;
};
export type LayerName = keyof LayerReducerState;

export type StudioReducerState = CoreReducerState & {
  undoState: CoreReducerState[];
  redoState: CoreReducerState[];
};

export type PasteActionPayload = { x: number; y: number } | undefined;

const LAYER_STORAGE_KEY = 'studio-layer-filters';

function loadSavedLayers(): Partial<LayerReducerState> | null {
  try {
    const stored = localStorage.getItem(LAYER_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // localStorage can throw in Safari private browsing or restricted iframes
  }
  return null;
}

function saveLayers(layers: LayerReducerState) {
  try {
    localStorage.setItem(LAYER_STORAGE_KEY, JSON.stringify(layers));
  } catch {
    // localStorage can throw in Safari private browsing or restricted iframes
  }
}

// a function just reduces the risk of accidentally reusing the same object...
export function getEmptyState(): StudioReducerState {
  const state: StudioReducerState = {
    initialized: false,
    highlightRelatedDevices: false,
    undoState: [],
    redoState: [],
    base: {
      sensors: [],
      rooms: [],
      zones: [],
      hives: [],
      unplotted: [],
      floorplans: [],
      spots: [],
      sensors3p: [],
      gateways3p: [],
    },
    modified: {
      sensors: {},
      rooms: {},
      zones: {},
      hives: {},
      floorplans: {},
      spots: {},
      sensors3p: {},
      gateways3p: {},
    },
    added: {
      sensors: [],
      rooms: [],
      zones: [],
      hives: [],
      floorplans: [],
      spots: [],
      sensors3p: [],
      gateways3p: [],
    },
    deleted: [],
    archiveChanges: {},
    selectedHovered: false,
    selected: [],
    editing: [],
    errors: {},
    heatmapStyle: 'interpolated',
    layers: {
      sensors: {
        visible: true,
        locked: false,
      },
      rooms: {
        visible: true,
        locked: false,
      },
      zones: {
        visible: true,
        locked: false,
      },
      hives: {
        visible: true,
        locked: false,
      },
      floorplans: {
        visible: true,
        locked: true,
      },
      spots: {
        visible: true,
        locked: false,
      },
      archivedDevices: {
        visible: false,
        locked: false,
      },
      heatmaps: {
        visible: false,
        locked: false,
      },
      detections: {
        visible: false,
        locked: false,
      },
      sensorModel: {
        visible: false,
        locked: false, // This won't be used
      },
      hiveModel: {
        visible: false,
        locked: false, // This won't be used
      },
      batteryStatus: {
        visible: false,
        locked: false,
      },
      sensors3p: {
        visible: true,
        locked: false,
      },
      gateways3p: {
        visible: true,
        locked: false,
      },
    },
    search: {
      text: '',
      hoverItemId: null,
    },
    clipboard: null,
    currentFloorId: null,
  };

  const saved = loadSavedLayers();
  if (saved) {
    const savedLayers = saved;
    for (const key of Object.keys(savedLayers) as LayerName[]) {
      if (state.layers[key] && savedLayers[key]) {
        state.layers[key] = { ...state.layers[key], ...savedLayers[key] };
      }
    }
  }

  return state;
}

const historyCapacity = 20;

/**
 * Given the previous state, updates the undo state and clears the redo state.
 */
function updateHistory({
  undoState,

  redoState,
  ...prevState
}: StudioReducerState): StudioReducerState {
  return {
    ...prevState,
    redoState: [],
    undoState: [...undoState, prevState].slice(-historyCapacity),
  };
}

/**
 * Updates selected items using updater functions that return the
 * new modified state to be merged with the existing modified state.
 */
function updateSelected({
  prevState,
  sensorUpdateFunc,
  roomUpdateFunc,
  zoneUpdateFunc,
  hiveUpdateFunc,
  floorplanUpdateFunc,
  spotUpdateFunc,
  sensor3pUpdateFunc,
  gateway3pUpdateFunc,
}: {
  prevState: StudioReducerState;
  sensorUpdateFunc?: (s: Sensor) => Partial<Sensor>;
  roomUpdateFunc?: (r: Room) => Partial<Room>;
  zoneUpdateFunc?: (z: Zone) => Partial<Zone>;
  hiveUpdateFunc?: (h: Hive) => Partial<Hive>;
  floorplanUpdateFunc?: (f: AugmentedFloorPlan) => Partial<AugmentedFloorPlan>;
  spotUpdateFunc?: (s: Spot) => Partial<Spot>;
  sensor3pUpdateFunc?: (s: Sensor3P) => Partial<Sensor3P>;
  gateway3pUpdateFunc?: (g: Gateway3P) => Partial<Gateway3P>;
}): StudioReducerState {
  const { selected, base, added, modified } = prevState;
  const selectedSensors = selected
    .map((id) => {
      const sensor = [...base.sensors, ...added.sensors].find((s) => s.id === id);
      return sensor ? { ...sensor, ...modified.sensors[id] } : null;
    })
    .filter((s): s is Sensor => !!s);
  const selectedRooms = selected
    .map((id) => {
      const room = [...base.rooms, ...added.rooms].find((r) => r.id === id);
      return room ? { ...room, ...modified.rooms[id] } : null;
    })
    .filter((r): r is Room => !!r);
  const selectedZones = selected
    .map((id) => {
      const zone = [...base.zones, ...added.zones].find((z) => z.id === id);
      return zone ? { ...zone, ...modified.zones[id] } : null;
    })
    .filter((z): z is Zone => !!z);
  const selectedHives = selected
    .map((id) => {
      const hive = [...base.hives, ...added.hives].find((h) => h.id === id);
      return hive ? { ...hive, ...modified.hives[id] } : null;
    })
    .filter((h): h is Hive => !!h);
  const selectedFloorplans = selected
    .map((id) => {
      const floorplan = [...base.floorplans, ...added.floorplans].find((f) => f.id === id);
      return floorplan ? { ...floorplan, ...modified.floorplans[id] } : null;
    })
    .filter((f): f is AugmentedFloorPlan => !!f);
  const selectedSpots = selected
    .map((id) => {
      const spot = [...base.spots, ...added.spots].find((s) => s.id === id);
      return spot ? { ...spot, ...modified.spots[id] } : null;
    })
    .filter((s): s is Spot => !!s);
  const selectedSensors3p = selected
    .map((id) => {
      const sensor3p = [...base.sensors3p, ...added.sensors3p].find((s) => s.id === id);
      return sensor3p ? { ...sensor3p, ...modified.sensors3p[id] } : null;
    })
    .filter((s): s is Sensor3P => !!s);
  const selectedGateways3p = selected
    .map((id) => {
      const gateway3p = [...base.gateways3p, ...added.gateways3p].find((g) => g.id === id);
      return gateway3p ? { ...gateway3p, ...modified.gateways3p[id] } : null;
    })
    .filter((g): g is Gateway3P => !!g);
  return {
    ...prevState,
    modified: {
      ...prevState.modified,
      sensors: {
        ...modified.sensors,
        ...(sensorUpdateFunc &&
          Object.fromEntries(
            selectedSensors.map((s) => [
              s.id,
              { ...modified.sensors[s.id], ...sensorUpdateFunc(s) },
            ]),
          )),
      },
      rooms: {
        ...modified.rooms,
        ...(roomUpdateFunc &&
          Object.fromEntries(
            selectedRooms.map((r) => [r.id, { ...modified.rooms[r.id], ...roomUpdateFunc(r) }]),
          )),
      },
      zones: {
        ...modified.zones,
        ...(zoneUpdateFunc &&
          Object.fromEntries(
            selectedZones.map((z) => [z.id, { ...modified.zones[z.id], ...zoneUpdateFunc(z) }]),
          )),
      },
      hives: {
        ...modified.hives,
        ...(hiveUpdateFunc &&
          Object.fromEntries(
            selectedHives.map((h) => [h.id, { ...modified.hives[h.id], ...hiveUpdateFunc(h) }]),
          )),
      },
      floorplans: {
        ...modified.floorplans,
        ...(floorplanUpdateFunc &&
          Object.fromEntries(
            selectedFloorplans.map((f) => [
              f.id,
              { ...modified.floorplans[f.id], ...floorplanUpdateFunc(f) },
            ]),
          )),
      },
      spots: {
        ...modified.spots,
        ...(spotUpdateFunc &&
          Object.fromEntries(
            selectedSpots.map((s) => [s.id, { ...modified.spots[s.id], ...spotUpdateFunc(s) }]),
          )),
      },
      sensors3p: {
        ...modified.sensors3p,
        ...(sensor3pUpdateFunc &&
          Object.fromEntries(
            selectedSensors3p.map((s) => [
              s.id,
              { ...modified.sensors3p[s.id], ...sensor3pUpdateFunc(s) },
            ]),
          )),
      },
      gateways3p: {
        ...modified.gateways3p,
        ...(gateway3pUpdateFunc &&
          Object.fromEntries(
            selectedGateways3p.map((g) => [
              g.id,
              { ...modified.gateways3p[g.id], ...gateway3pUpdateFunc(g) },
            ]),
          )),
      },
    },
  };
}

/** Resolve a single sensor by merging base/added with modified state. */
function resolveSensor(sensorId: string, state: CoreReducerState): Sensor | undefined {
  const item =
    state.added.sensors.find((s) => s.id === sensorId) ??
    state.base.sensors.find((s) => s.id === sensorId);
  if (!item) return undefined;
  return { ...item, ...state.modified.sensors[sensorId] };
}

/** Resolve selected items by looking up IDs in base+added, merging modified state, and optionally filtering. */
function resolveSelectedItems<T extends { id: string }>(
  selected: string[],
  base: T[],
  added: T[],
  modified: Record<string, Partial<T>>,
  filter?: (item: T) => boolean,
): T[] {
  const lookup = new Map<string, T>();
  for (const item of base) lookup.set(item.id, item);
  for (const item of added) lookup.set(item.id, item);

  return selected
    .map((id) => {
      const item = lookup.get(id);
      return item ? ({ ...item, ...modified[id] } as T) : null;
    })
    .filter((r): r is T => !!r && (filter ? filter(r) : true));
}

/**
 * Compute the next sequence index for a clone/mirror sensor by finding the max seq
 * across saved MAC addresses (from backend) and unsaved added sensors (from Redux state).
 */
function getNextVirtualSeq(
  savedMacs: string[],
  addedSensors: Sensor[],
  sourceMac: string,
  kind: 'clone' | 'mirror',
): number {
  const prefix = kind === 'clone' ? 'fake' : 'mirror';
  const flag = kind === 'clone' ? 'isClone' : 'isMirror';
  const savedMaxSeq = Math.max(0, ...savedMacs.map((mac) => extractSeqFromMac(mac, prefix)));
  const unsavedMaxSeq = Math.max(
    0,
    ...addedSensors
      .filter(
        (s): s is typeof s & { macAddress: string } =>
          !!s[flag] && s.sourceMacAddress === sourceMac && !!s.macAddress,
      )
      .map((s) => extractSeqFromMac(s.macAddress, prefix)),
  );
  return Math.max(savedMaxSeq, unsavedMaxSeq) + 1;
}

type Clipboard = NonNullable<CoreReducerState['clipboard']>;

/** Restore items with offset, split into base vs added, build modified entries */
function restoreCutItems<T extends { id: string }>(
  clipboardItems: T[],
  baseItems: T[],
  prevAdded: T[],
  prevModified: Record<string, Partial<T>>,
  applyOffset: (item: T) => T,
  extractCoordPatch: (item: T) => Partial<T>,
) {
  const baseIdSet = new Set(baseItems.map((i) => i.id));
  const restored = clipboardItems.map(applyOffset);
  const modified = { ...prevModified };
  for (const item of restored) {
    if (baseIdSet.has(item.id)) {
      modified[item.id] = { ...modified[item.id], ...extractCoordPatch(item) };
    }
  }
  return {
    added: [...prevAdded, ...restored.filter((i) => !baseIdSet.has(i.id))],
    modified,
  };
}

/** Cut+Paste: restore original items (move operation) with updated coordinates */
function handleCutPaste(
  prevState: StudioReducerState,
  clipboard: Clipboard,
  allItems: { id: string }[],
  offsetX: number,
  offsetY: number,
): StudioReducerState {
  const clipboardIdSet = new Set(allItems.map((item) => item.id));

  const rooms = restoreCutItems(
    clipboard.rooms,
    prevState.base.rooms,
    prevState.added.rooms,
    prevState.modified.rooms,
    (room) => ({
      ...room,
      coordinates: room.coordinates.map(([cx, cy]) => [
        cx + offsetX,
        cy + offsetY,
      ]) as Room['coordinates'],
    }),
    (room) => ({ coordinates: room.coordinates }) as Partial<(typeof clipboard.rooms)[number]>,
  );

  const zones = restoreCutItems(
    clipboard.zones,
    prevState.base.zones,
    prevState.added.zones,
    prevState.modified.zones,
    (zone) => ({
      ...zone,
      coordinates: zone.coordinates.map(([cx, cy]) => [
        cx + offsetX,
        cy + offsetY,
      ]) as Zone['coordinates'],
    }),
    (zone) => ({ coordinates: zone.coordinates }) as Partial<(typeof clipboard.zones)[number]>,
  );

  const sensors = restoreCutItems(
    clipboard.sensors,
    prevState.base.sensors,
    prevState.added.sensors,
    prevState.modified.sensors,
    (sensor) => ({
      ...sensor,
      center: [sensor.center[0] + offsetX, sensor.center[1] + offsetY] as TXYPoint,
    }),
    (sensor) => ({ center: sensor.center }) as Partial<(typeof clipboard.sensors)[number]>,
  );

  const spots = restoreCutItems(
    clipboard.spots,
    prevState.base.spots,
    prevState.added.spots,
    prevState.modified.spots,
    (spot) => ({
      ...spot,
      location: [spot.location[0] + offsetX, spot.location[1] + offsetY] as [number, number],
    }),
    (spot) => ({ location: spot.location }) as Partial<(typeof clipboard.spots)[number]>,
  );

  return {
    ...updateHistory(prevState),
    clipboard: null,
    deleted: prevState.deleted.filter((id) => !clipboardIdSet.has(id)),
    selected: [...clipboardIdSet],
    modified: {
      ...prevState.modified,
      rooms: rooms.modified,
      zones: zones.modified,
      sensors: sensors.modified,
      spots: spots.modified,
    },
    added: {
      ...prevState.added,
      rooms: rooms.added,
      zones: zones.added,
      sensors: sensors.added,
      spots: spots.added,
    },
  };
}

function createAddSensorReducer(model: ESensorModel) {
  return (prevState: StudioReducerState, action: { payload: { x: number; y: number } }) => {
    const { x, y } = action.payload;
    const {
      added: { sensors },
    } = prevState;
    const sensorId = generateId.sensor();
    const totalSensors = prevState.base.sensors.length + prevState.added.sensors.length;
    return {
      ...updateHistory(prevState),
      selected: [sensorId],
      added: {
        ...prevState.added,
        sensors: [
          ...sensors,
          {
            name: `Sensor ${totalSensors + 1}`,
            id: sensorId,
            center: [x, y] as [number, number],
            mode: ESensorMode.PRESENCE,
            macAddress: '',
            orientation: [0, 0, 0] as [number, number, number],
            fov: getFovForModel(model),
            height: 3,
            model,
            powerType: 'Battery' as const,
          },
        ],
      },
    };
  };
}

const studioSlice = createSlice({
  initialState: getEmptyState(),
  name: 'studio',
  reducers: {
    select: (prevState, action: { payload: { id: string } }) => {
      const { id } = action.payload;
      return { ...prevState, selected: [id] };
    },
    multiSelect: (prevState, action: { payload: { ids: string[] } }) => {
      const { ids } = action.payload;
      return { ...prevState, selected: ids };
    },
    deselect: (prevState, action: { payload: { id: string } }) => {
      const { id } = action.payload;
      const selected = new Set<string>(prevState.selected);
      selected.delete(id);
      return { ...prevState, selected: Array.from(selected) };
    },
    toggleSelect: (prevState, action: { payload: { id: string } }) => {
      const { id } = action.payload;
      const selected = new Set<string>(prevState.selected);
      if (selected.has(id)) {
        selected.delete(id);
      } else {
        selected.add(id);
      }
      return { ...prevState, selected: Array.from(selected) };
    },
    appendSelect: (prevState, action: { payload: { ids: string[] } }) => {
      const { ids } = action.payload;
      const selected = new Set<string>([...prevState.selected, ...ids]);
      return { ...prevState, selected: Array.from(selected) };
    },
    deselectAll: (prevState) => {
      return { ...prevState, selected: [] };
    },
    enterEdit: (prevState, action: { payload: { id: string } }) => {
      const { id } = action.payload;
      const editing = new Set<string>(prevState.editing);
      editing.add(id);
      return { ...prevState, editing: Array.from(editing) };
    },
    exitEdit: (prevState, action: { payload: { id: string } }) => {
      const { id } = action.payload;
      const editing = new Set<string>(prevState.editing);
      editing.delete(id);
      return { ...prevState, editing: Array.from(editing) };
    },
    exitAllEdit: (prevState) => {
      return { ...prevState, editing: [] };
    },
    move: (prevState, action: { payload: { x: number; y: number } }) => {
      const { x, y } = action.payload;
      return updateSelected({
        prevState: updateHistory(prevState),
        sensorUpdateFunc: (s) => {
          const { center } = SensorObject.fromSensor(s).translate(x, y);
          return {
            center,
          };
        },
        roomUpdateFunc: (r) => {
          const { coordinates } = RoomObject.fromRoom(r).translate(x, y);
          return {
            coordinates: ensureCoordinateType(coordinates, r.coordinates),
          };
        },
        zoneUpdateFunc: (z) => {
          const { coordinates } = RoomObject.fromRoom(z).translate(x, y);
          return {
            coordinates: ensureCoordinateType(coordinates, z.coordinates),
          };
        },
        hiveUpdateFunc: (h) => {
          const { center } = HiveObject.fromHive(h).translate(x, y);
          return {
            coordinates: center,
          };
        },
        floorplanUpdateFunc: (f) => {
          const { coordinates } = FloorPlanObject.fromFloorPlan(f).translate(x, y);
          return {
            coordinates,
          };
        },
        spotUpdateFunc: (s) => ({
          location: [s.location[0] + x, s.location[1] + y] as [number, number],
        }),
        sensor3pUpdateFunc: (s) => ({
          location: [s.location[0] + x, s.location[1] + y] as [number, number],
        }),
        gateway3pUpdateFunc: (g) => ({
          location: [g.location[0] + x, g.location[1] + y] as [number, number],
        }),
      });
    },
    resize: (
      prevState,
      action: { payload: { sx: number; sy: number; anchor: TXYPoint; rotation: number } },
    ) => {
      const { sx, sy, anchor, rotation } = action.payload;
      return updateSelected({
        prevState: updateHistory(prevState),
        roomUpdateFunc: (r) => {
          const { coordinates } = RoomObject.fromRoom(r).scale(sx, sy, anchor, rotation);
          return {
            coordinates: ensureCoordinateType(coordinates, r.coordinates),
          };
        },
        zoneUpdateFunc: (z) => {
          const { coordinates } = RoomObject.fromRoom(z).scale(sx, sy, anchor, rotation);
          return {
            coordinates: ensureCoordinateType(coordinates, z.coordinates),
          };
        },
        floorplanUpdateFunc: (f) => {
          const { coordinates } = FloorPlanObject.fromFloorPlan(f).scale(sx, sy, anchor, rotation);
          return {
            coordinates,
          };
        },
      });
    },
    rotate: (prevState, action: { payload: { angle: number; origin: TXYPoint } }) => {
      const { angle, origin } = action.payload;
      return updateSelected({
        prevState: updateHistory(prevState),
        sensorUpdateFunc: (s) => {
          const { center, rotation } = SensorObject.fromSensor(s).rotate(angle, origin);
          return {
            center,
            orientation: [s.orientation[0], rotation, s.orientation[2]],
          };
        },
        hiveUpdateFunc: (h) => {
          const { center } = HiveObject.fromHive(h).rotate(angle, origin);
          return {
            coordinates: center,
          };
        },
        roomUpdateFunc: (r) => {
          const { coordinates, rotation } = RoomObject.fromRoom(r).rotate(angle, origin);
          return {
            coordinates: ensureCoordinateType(coordinates, r.coordinates),
            rotation,
          };
        },
        zoneUpdateFunc: (z) => {
          const { coordinates, rotation } = RoomObject.fromRoom(z).rotate(angle, origin);
          return {
            coordinates: ensureCoordinateType(coordinates, z.coordinates),
            rotation,
          };
        },
        floorplanUpdateFunc: (f) => {
          const { coordinates, rotation } = FloorPlanObject.fromFloorPlan(f).rotate(angle, origin);
          return {
            coordinates,
            rotation,
          };
        },
        spotUpdateFunc: (s) => ({
          location: rotatePointAroundOrigin(s.location, origin, -angle),
        }),
        sensor3pUpdateFunc: (s) => ({
          location: rotatePointAroundOrigin(s.location, origin, -angle),
        }),
        gateway3pUpdateFunc: (g) => ({
          location: rotatePointAroundOrigin(g.location, origin, -angle),
        }),
      });
    },
    addSensor: createAddSensorReducer(ESensorModel.HEATIC2_PLUS),
    addSensorH3: createAddSensorReducer(ESensorModel.HEATIC3),
    bulkAddSensors: (
      prevState,
      action: { payload: { x: number; y: number; sensors: HiveInfoUnplottedSensorFragment[] } },
    ) => {
      const { x, y, sensors } = action.payload;
      const {
        base,
        modified,
        deleted,
        added: { sensors: prevSensors },
      } = prevState;

      const defaultHeight = 3;
      const totalSensors = prevState.base.sensors.length + prevState.added.sensors.length;
      const newSensors = sensors
        .filter(({ id }) => !base.sensors.find((s) => s.id === id))
        .map(({ macAddress, model, mode, hive, id }, index) => {
          const sensorModel = model ? (model as ESensorModel) : ESensorModel.UNKNOWN;
          const sensorFov = getFovForModel(sensorModel);
          return {
            name: `Sensor ${totalSensors + 1 + index}`,
            id: id || generateId.sensor(),
            center: [
              x + index * 1.2 * calculateSensorCoverage({ height: defaultHeight, fov: sensorFov }),
              y,
            ] as TXYPoint,
            mode: (mode as ESensorMode) || ESensorMode.PRESENCE,
            macAddress: macAddress || '',
            orientation: [0, 0, 0] as [number, number, number],
            fov: sensorFov,
            height: defaultHeight,
            parallelToDoor: mode === ESensorMode.TRAFFIC ? true : undefined,
            inDirection: mode === ESensorMode.TRAFFIC ? (1 as TInDirection) : undefined,
            doorLine: mode === ESensorMode.TRAFFIC ? 0.15 : undefined,
            model: sensorModel,
            hive: hive ?? undefined,
            powerType: 'Battery' as ESensorPowerType,
          };
        });

      const modifiedSensors = sensors
        .filter(({ id }) => base.sensors.find((s) => s.id === id))
        .map(({ id = generateId.sensor(), height = defaultHeight, fov, model }, index) => {
          const sensorFov = fov ?? getFovForModel(model);
          return {
            id,
            center: [
              x +
                (index + newSensors.length) *
                  1.2 *
                  calculateSensorCoverage({ height, fov: sensorFov }),
              y,
            ] as TXYPoint,
          };
        });

      const sensorIds = [...newSensors.map((s) => s.id), ...modifiedSensors.map((s) => s.id)];
      return {
        ...updateHistory(prevState),
        selected: Array.from(new Set<string>(sensorIds)),
        modified: {
          ...modified,
          sensors: modifiedSensors.reduce(
            (acc, { id, ...rest }) => ({ ...acc, [id]: rest }),
            modified.sensors,
          ),
        },
        added: {
          ...prevState.added,
          sensors: [...prevSensors, ...newSensors],
        },
        // We need to remove the modified sensor from deleted list
        deleted: deleted.filter((id) => !modifiedSensors.find((s) => s.id === id)),
      };
    },
    addFloorPlan: (prevState, action: { payload: { base64: string; aspectRatio: number } }) => {
      const { base64, aspectRatio } = action.payload;
      const {
        added: { floorplans },
      } = prevState;
      const localFloorplanId = generateId.floorplan();
      const width = 50;
      const height = width / (aspectRatio || 1);
      return {
        ...updateHistory(prevState),
        selected: [localFloorplanId],
        added: {
          ...prevState.added,
          floorplans: [
            ...floorplans,
            {
              name: 'New floorplan',
              id: localFloorplanId,
              base64: base64,
              url: '',
              aspectRatioLocked: true,
              opacity: 1,
              coordinates: rectToPoints(0, 0, width, height),
              rotation: 0,
            },
          ],
        },
      };
    },
    addRoom: (
      prevState,
      action: { payload: { x: number; y: number; width: number; height: number } },
    ) => {
      const { x, y, width, height } = action.payload;
      const {
        added: { rooms },
      } = prevState;
      const roomId = generateId.room();
      const totalRooms = prevState.base.rooms.length + prevState.added.rooms.length;
      return {
        ...updateHistory(prevState),
        selected: [roomId],
        added: {
          ...prevState.added,
          rooms: [
            ...rooms,
            {
              name: `Room ${totalRooms + 1}`,
              id: roomId,
              coordinates: rectToPoints(x, y, width, height),
              capacity: {
                max: 0,
                mid: 0,
              },
              rotation: 0,
              sensors: [],
            },
          ],
        },
      };
    },
    modifySensors: (
      prevState,
      action: {
        payload: {
          data: (Partial<Sensor> & { id: string })[];
          skipUpdateHistory?: boolean;
        };
      },
    ) => {
      datadogRum.addAction('Modify sensors', { source: 'studio-on-web' });
      const {
        payload: { data: modifiedSensors, skipUpdateHistory },
      } = action;
      const {
        modified: { sensors },
      } = prevState;

      // Cascade source sensor changes to linked virtual sensors.
      // Only the locked properties (height, direction, doorline, fov, mode) propagate.
      const cascadeKeys = ['height', 'inDirection', 'doorLine', 'parallelToDoor', 'fov', 'mode'];
      const cascadeEntries: [string, Partial<Sensor>][] = [];
      const directlyModifiedIds = new Set(modifiedSensors.map((s) => s.id));

      const allSensors = [...prevState.base.sensors, ...prevState.added.sensors].filter(
        (s) => !prevState.deleted.includes(s.id),
      );

      // Build sourceMac → virtual sensors lookup for O(1) cascade instead of O(N) scan per mod
      const virtualsBySource = new Map<string, typeof allSensors>();
      for (const s of allSensors) {
        const effectiveSource = sensors[s.id]?.sourceMacAddress ?? s.sourceMacAddress;
        const effectiveIsMirror = sensors[s.id]?.isMirror ?? s.isMirror;
        const effectiveIsClone = sensors[s.id]?.isClone ?? s.isClone;
        if (effectiveSource && (effectiveIsMirror || effectiveIsClone)) {
          const arr = virtualsBySource.get(effectiveSource);
          if (arr) arr.push(s);
          else virtualsBySource.set(effectiveSource, [s]);
        }
      }

      for (const mod of modifiedSensors) {
        const changed = cascadeKeys.filter((k) => k in mod);
        if (changed.length === 0) continue;

        // Resolve the source sensor's MAC address
        const src =
          prevState.base.sensors.find((s) => s.id === mod.id) ||
          prevState.added.sensors.find((s) => s.id === mod.id);
        const sourceMac = sensors[mod.id]?.macAddress ?? src?.macAddress;
        if (!sourceMac) continue;

        const linked = virtualsBySource.get(sourceMac) ?? [];

        for (const virtual of linked) {
          // Skip virtuals that were directly modified in this batch — user intent takes priority
          if (directlyModifiedIds.has(virtual.id)) continue;
          const effectiveIsMirror = sensors[virtual.id]?.isMirror ?? virtual.isMirror;
          const update: Partial<Sensor> = {};
          for (const key of changed) {
            switch (key) {
              case 'height':
                if (mod.height != null) update.height = mod.height;
                break;
              case 'fov':
                if (mod.fov != null) update.fov = mod.fov;
                break;
              case 'doorLine':
                if (mod.doorLine != null) update.doorLine = mod.doorLine;
                break;
              case 'parallelToDoor':
                if (mod.parallelToDoor != null) update.parallelToDoor = mod.parallelToDoor;
                break;
              case 'inDirection':
                if (mod.inDirection === 0 || mod.inDirection === 1) {
                  update.inDirection = effectiveIsMirror
                    ? flipInDirection(mod.inDirection)
                    : mod.inDirection;
                }
                break;
              case 'mode':
                // Mirrors are always TRAFFIC — only cascade mode to clones
                if (!effectiveIsMirror && mod.mode) {
                  update.mode = mod.mode;
                  // Auto-toggle PIR: off for traffic, on for presence (H2+ requirement)
                  // Use effective config (pending edits take priority over base)
                  const effectiveConfig = sensors[virtual.id]?.config ?? virtual.config ?? {};
                  if (mod.mode === ESensorMode.TRAFFIC) {
                    update.config = { ...effectiveConfig, use_pir: false };
                  } else if (mod.mode === ESensorMode.PRESENCE) {
                    update.config = { ...effectiveConfig, use_pir: true };
                  }
                }
                break;
            }
          }
          if (Object.keys(update).length > 0) {
            // sensors[virtual.id] is partial modifications (not a full Sensor), so spreading
            // it preserves any prior pending edits while overlaying the cascaded changes.
            cascadeEntries.push([virtual.id, { ...sensors[virtual.id], ...update }]);
          }
        }
      }

      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          sensors: {
            ...sensors,
            ...Object.fromEntries(
              modifiedSensors.map((s) => [
                s.id,
                { ...sensors[s.id], ...omit(s, 'distanceToRoomBorder') },
              ]),
            ),
            // Cascade entries are spread AFTER direct modifications. This is safe because
            // directlyModifiedIds prevents cascading to sensors the user edited in this batch.
            // If that guard is ever removed, reverse the spread order so user edits take priority.
            ...Object.fromEntries(cascadeEntries),
          },
        },
      };
    },
    setSensorVirtualType: (
      prevState,
      action: {
        payload: {
          sensorId: string;
          virtualType: 'physical' | 'mirror' | 'clone';
        };
      },
    ) => {
      const { sensorId, virtualType } = action.payload;

      // Only operates on sensors in added.sensors (not yet persisted to the server).
      // Index is valid after updateHistory because it shallow-copies added.sensors.
      const sensorIndex = prevState.added.sensors.findIndex((s) => s.id === sensorId);
      if (sensorIndex === -1) {
        const msg = `[studio] setSensorVirtualType: sensor ${sensorId} not found in added sensors`;
        console.warn(msg);
        datadogRum.addError(new Error(msg), { source: 'studio-on-web' });
        return prevState;
      }

      const newState = updateHistory(prevState);

      const sensor = newState.added.sensors[sensorIndex];
      const updatedSensors = [...newState.added.sensors];

      switch (virtualType) {
        case 'physical':
          updatedSensors[sensorIndex] = {
            ...sensor,
            isMirror: undefined,
            isClone: undefined,
            sourceMacAddress: undefined,
            sourceLockedByContextMenu: undefined,
            mode: ESensorMode.PRESENCE,
          };
          break;
        case 'mirror':
          updatedSensors[sensorIndex] = {
            ...sensor,
            isMirror: true,
            isClone: undefined,
            sourceMacAddress: undefined,
            sourceLockedByContextMenu: undefined,
            mode: ESensorMode.TRAFFIC,
          };
          break;
        case 'clone':
          updatedSensors[sensorIndex] = {
            ...sensor,
            isMirror: undefined,
            isClone: true,
            sourceMacAddress: undefined,
            sourceLockedByContextMenu: undefined,
          };
          break;
      }

      // Clear mode from modified.sensors so it doesn't override the mode set by
      // the virtual type switch (mirrors force TRAFFIC, clones keep existing mode)
      const updatedModifiedSensors = { ...newState.modified.sensors };
      if (updatedModifiedSensors[sensorId]) {
        const { mode: _mode, ...rest } = updatedModifiedSensors[sensorId];
        updatedModifiedSensors[sensorId] = rest;
      }

      return {
        ...newState,
        modified: {
          ...newState.modified,
          sensors: updatedModifiedSensors,
        },
        added: {
          ...newState.added,
          sensors: updatedSensors,
        },
      };
    },
    // Links a virtual sensor to a source. For mirrors: forces TRAFFIC mode, flips direction.
    // For clones: inherits mode and direction from source. Both inherit height, orientation,
    // doorLine, parallelToDoor, and fov. Clears modified.sensors entries for inherited fields
    // to prevent stale overrides.
    linkVirtualSensor: (
      prevState,
      action: {
        payload: {
          sensorId: string;
          sourceMacAddress: string;
          sourceName: string;
          sourceMode: ESensorMode;
          sourceHeight: number;
          sourceInDirection?: TInDirection;
          sourceOrientation?: [number, number, number];
          sourceDoorLine?: number;
          sourceParallelToDoor?: boolean;
          sourceFov?: number;
          sourceModel?: ESensorModel;
          sourcePowerType?: 'Wired' | 'Battery';
          sourceConfig?: { use_pir?: boolean };
        };
      },
    ) => {
      const {
        sensorId,
        sourceMacAddress,
        sourceName,
        sourceMode,
        sourceHeight,
        sourceInDirection,
        sourceOrientation,
        sourceDoorLine,
        sourceParallelToDoor,
        sourceFov,
        sourceModel,
        sourcePowerType,
        sourceConfig,
      } = action.payload;

      const sensorIndex = prevState.added.sensors.findIndex((s) => s.id === sensorId);
      if (sensorIndex === -1) {
        const msg = `[studio] linkVirtualSensor: sensor ${sensorId} not found in added sensors`;
        console.warn(msg);
        datadogRum.addError(new Error(msg), { source: 'studio-on-web' });
        return prevState;
      }

      const sensor = prevState.added.sensors[sensorIndex];
      if (!sensor.isMirror && !sensor.isClone) {
        const msg = `[studio] linkVirtualSensor: sensor ${sensorId} is neither mirror nor clone`;
        console.warn(msg);
        datadogRum.addError(new Error(msg), { source: 'studio-on-web' });
        return prevState;
      }

      const newState = updateHistory(prevState);

      const updatedSensors = [...newState.added.sensors];

      // Explicitly set all inherited fields. Optional fields (orientation, fov, model)
      // use conditional spread — if the new source doesn't provide them, the virtual
      // sensor retains its previous values rather than being cleared. This is intentional:
      // these fields have no sensible default, and keeping the prior value is safer than
      // resetting to undefined on a required Sensor property.
      const sharedSourceFields = {
        sourceMacAddress,
        height: sourceHeight,
        ...(sourceOrientation && { orientation: sourceOrientation }),
        ...(sourceFov !== undefined && { fov: sourceFov }),
        ...(sourceModel && { model: sourceModel }),
        doorLine: sourceDoorLine,
        parallelToDoor: sourceParallelToDoor,
        ...(sourcePowerType && { powerType: sourcePowerType }),
        ...(sourceConfig && { config: sourceConfig }),
      };

      const newSensor = newState.added.sensors[sensorIndex];
      if (newSensor.isMirror) {
        updatedSensors[sensorIndex] = {
          ...newSensor,
          ...sharedSourceFields,
          name: getDefaultMirrorName(sourceName),
          mode: ESensorMode.TRAFFIC,
          inDirection: sourceInDirection !== undefined ? flipInDirection(sourceInDirection) : 0,
        };
      } else {
        updatedSensors[sensorIndex] = {
          ...newSensor,
          ...sharedSourceFields,
          name: getDefaultCloneName(sourceName),
          mode: sourceMode,
          inDirection: sourceInDirection ?? newSensor.inDirection ?? 0,
        };
      }

      // Clear these fields from modified.sensors so they don't override inherited values
      const updatedModifiedSensors = { ...newState.modified.sensors };
      if (updatedModifiedSensors[sensorId]) {
        const {
          height: _h,
          orientation: _o,
          doorLine: _d,
          parallelToDoor: _p,
          fov: _f,
          mode: _m,
          inDirection: _i,
          ...rest
        } = updatedModifiedSensors[sensorId];
        updatedModifiedSensors[sensorId] = rest;
      }

      return {
        ...newState,
        modified: {
          ...newState.modified,
          sensors: updatedModifiedSensors,
        },
        added: {
          ...newState.added,
          sensors: updatedSensors,
        },
      };
    },
    // Creates a virtual sensor (mirror or clone) directly from a source sensor on the same floor.
    // Combines addSensor + setSensorVirtualType + linkVirtualSensor into one atomic, undoable action.
    // Used by the right-click context menu on eligible source sensors.
    createVirtualFromSource: (
      prevState,
      action: {
        payload: {
          sourceSensorId: string;
          virtualType: 'mirror' | 'clone';
        };
      },
    ) => {
      const { sourceSensorId, virtualType } = action.payload;

      // Find the source sensor in base or added
      const source =
        prevState.base.sensors.find((s) => s.id === sourceSensorId) ||
        prevState.added.sensors.find((s) => s.id === sourceSensorId);
      if (!source) return prevState;

      // Source must have a MAC and not be virtual
      if (!source.macAddress || source.isMirror || source.isClone) return prevState;

      // Apply any pending modifications to get effective source values
      const sourceMods = prevState.modified.sensors[sourceSensorId] ?? {};
      const effectiveSource = { ...source, ...sourceMods };

      // Mirrors require traffic mode (check after applying modifications)
      if (virtualType === 'mirror' && effectiveSource.mode !== ESensorMode.TRAFFIC)
        return prevState;

      const newState = updateHistory(prevState);
      const sensorId = generateId.sensor();

      // Offset the new sensor slightly so it doesn't overlap the source
      const offset = 0.5;
      const center: TXYPoint = [
        effectiveSource.center[0] + offset,
        effectiveSource.center[1] + offset,
      ];

      const sharedFields = {
        height: effectiveSource.height,
        orientation: effectiveSource.orientation,
        fov: effectiveSource.fov,
        doorLine: effectiveSource.doorLine,
        parallelToDoor: effectiveSource.parallelToDoor,
      };

      const isMirror = virtualType === 'mirror';
      const newSensor: Sensor = {
        id: sensorId,
        name: isMirror
          ? getDefaultMirrorName(effectiveSource.name)
          : getDefaultCloneName(effectiveSource.name),
        center,
        mode: isMirror ? ESensorMode.TRAFFIC : effectiveSource.mode,
        macAddress: '',
        model: effectiveSource.model,
        powerType: effectiveSource.powerType ?? 'Battery',
        config: effectiveSource.config,
        sourceMacAddress: effectiveSource.macAddress,
        sourceLockedByContextMenu: true,
        isMirror: isMirror ? true : undefined,
        isClone: isMirror ? undefined : true,
        inDirection: isMirror
          ? effectiveSource.inDirection !== undefined
            ? flipInDirection(effectiveSource.inDirection)
            : 0
          : effectiveSource.inDirection ?? 0,
        ...sharedFields,
      };

      return {
        ...newState,
        selected: [sensorId],
        added: {
          ...newState.added,
          sensors: [...newState.added.sensors, newSensor],
        },
      };
    },
    setCurrentFloorId: (prevState, action: { payload: string }) => {
      return { ...prevState, currentFloorId: action.payload };
    },
    moveToFloor: (
      prevState,
      action: {
        payload: {
          sensorIds: string[];
          targetFloorId: string;
        };
      },
    ) => {
      const { sensorIds, targetFloorId } = action.payload;
      if (targetFloorId === prevState.currentFloorId) return prevState;
      const newModifiedSensors = { ...prevState.modified.sensors };

      // Count how many sensors have already been moved to this target floor
      // so we can offset each new one progressively
      const alreadyMovedCount = Object.values(newModifiedSensors).filter(
        (m) => m.floorId === targetFloorId,
      ).length;

      for (let i = 0; i < sensorIds.length; i++) {
        const sensor = resolveSensor(sensorIds[i], prevState);
        if (!sensor) continue;

        // Offset each sensor progressively so they don't stack
        const idx = alreadyMovedCount + i;
        const offset = 3 + idx * 2;
        newModifiedSensors[sensorIds[i]] = {
          ...newModifiedSensors[sensorIds[i]],
          floorId: targetFloorId,
          center: [sensor.center[0] + offset, sensor.center[1] - offset] as [number, number],
        };
      }

      // Deselect moved sensors so they disappear immediately
      const movedSet = new Set(sensorIds);

      return {
        ...updateHistory(prevState),
        selected: prevState.selected.filter((id) => !movedSet.has(id)),
        modified: {
          ...prevState.modified,
          sensors: newModifiedSensors,
        },
      };
    },
    modifyRooms: (prevState, action: { payload: (Partial<Room> & { id: string })[] }) => {
      datadogRum.addAction('Modify rooms', { source: 'studio-on-web' });

      const { payload: modifiedRooms } = action;
      const {
        modified: { rooms },
      } = prevState;
      return {
        ...updateHistory(prevState),
        modified: {
          ...prevState.modified,
          rooms: {
            ...rooms,
            ...Object.fromEntries(modifiedRooms.map((r) => [r.id, { ...rooms[r.id], ...r }])),
          },
        },
      };
    },
    modifyFloorPlan: (
      prevState,
      action: {
        payload: {
          data: Partial<AugmentedFloorPlan> & { id: string };
          skipUpdateHistory?: boolean;
        };
      },
    ) => {
      datadogRum.addAction('Modify floor plan', { source: 'studio-on-web' });

      const {
        payload: { data, skipUpdateHistory },
      } = action;
      const {
        modified: { floorplans },
      } = prevState;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          floorplans: {
            ...floorplans,
            [data.id]: { ...floorplans[data.id], ...data },
          },
        },
      };
    },
    addZone: (
      prevState,
      action: {
        payload: {
          x: number;
          y: number;
          width: number;
          height: number;
          roomId?: string;
          name?: string;
        };
      },
    ) => {
      const { x, y, width, height, roomId } = action.payload;
      const {
        added: { zones },
      } = prevState;
      const zoneId = generateId.zone();
      const totalZones = prevState.base.zones.length + prevState.added.zones.length;
      return {
        ...updateHistory(prevState),
        selected: [zoneId],
        added: {
          ...prevState.added,
          zones: [
            ...zones,
            {
              name: `Zone ${totalZones + 1}`,
              room_id: roomId,
              id: zoneId,
              coordinates: rectToPoints(x, y, width, height),
              rotation: 0,
              capacity: {
                max: 0,
                mid: 0,
              },
            },
          ],
        },
      };
    },
    modifyZones: (prevState, action: { payload: Partial<Zone> & { id: string } }) => {
      datadogRum.addAction('Modify zones', { source: 'studio-on-web' });

      const { payload } = action;
      const id = payload.id;
      const {
        modified: { zones },
      } = prevState;
      return {
        ...updateHistory(prevState),
        modified: {
          ...prevState.modified,
          zones: {
            ...zones,
            [id]: { ...zones[id], ...payload },
          },
        },
      };
    },
    modifyRoom: (
      prevState,
      action: {
        payload:
          | (Partial<Room> & { id: string })
          | {
              data: Partial<Room> & { id: string };
              skipUpdateHistory?: boolean;
            };
      },
    ) => {
      datadogRum.addAction('Modify room', { source: 'studio-on-web' });

      // Support both old and new payload formats for backward compatibility
      const isNewFormat = 'data' in action.payload;
      const data = isNewFormat
        ? (action.payload as { data: Partial<Room> & { id: string }; skipUpdateHistory?: boolean })
            .data
        : (action.payload as Partial<Room> & { id: string });
      const skipUpdateHistory = isNewFormat
        ? (action.payload as { data: Partial<Room> & { id: string }; skipUpdateHistory?: boolean })
            .skipUpdateHistory ?? false
        : false;
      const id = data.id;
      const {
        modified: { rooms },
      } = prevState;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          rooms: {
            ...rooms,
            [id]: { ...rooms[id], ...data },
          },
        },
      };
    },
    modifyZone: (
      prevState,
      action: {
        payload:
          | (Partial<Zone> & { id: string })
          | {
              data: Partial<Zone> & { id: string };
              skipUpdateHistory?: boolean;
            };
      },
    ) => {
      datadogRum.addAction('Modify zone', { source: 'studio-on-web' });

      // Support both old and new payload formats for backward compatibility
      const isNewFormat = 'data' in action.payload;
      const data = isNewFormat
        ? (action.payload as { data: Partial<Zone> & { id: string }; skipUpdateHistory?: boolean })
            .data
        : (action.payload as Partial<Zone> & { id: string });
      const skipUpdateHistory = isNewFormat
        ? (action.payload as { data: Partial<Zone> & { id: string }; skipUpdateHistory?: boolean })
            .skipUpdateHistory ?? false
        : false;
      const id = data.id;
      const {
        modified: { zones },
      } = prevState;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          zones: {
            ...zones,
            [id]: { ...zones[id], ...data },
          },
        },
      };
    },
    addHive: (prevState, action: { payload: { x: number; y: number } }) => {
      const { x, y } = action.payload;
      const {
        base,
        added: { hives },
      } = prevState;
      // try to find an unplotted hive and use that first
      const stagedHive = base.unplotted.find((a) => !hives.some((b) => b.id === a.id));
      if (stagedHive) {
        const hiveId: string = stagedHive.id;
        return {
          ...updateHistory(prevState),
          selected: [hiveId],
          added: {
            ...prevState.added,
            hives: [
              ...hives,
              {
                name: stagedHive.name,
                id: hiveId,
                coordinates: [x, y],
                serial_number: stagedHive.serial_number,
                sensors: [],
                connectionHealth: {
                  status: '',
                  codes: [],
                },
                note: stagedHive.note,
              },
            ],
          },
        };
      }

      // otherwise, create a new hive
      const hiveId = generateId.hive();
      const totalHives = prevState.base.hives.length + prevState.added.hives.length;
      return {
        ...updateHistory(prevState),
        selected: [hiveId],
        added: {
          ...prevState.added,
          hives: [
            ...hives,
            {
              name: `Hive ${totalHives + 1}`,
              id: hiveId,
              coordinates: [x, y],
              serial_number: '',
              sensors: [],
              connectionHealth: {
                status: '',
                codes: [],
              },
            },
          ],
        },
      };
    },
    modifyHive: (
      prevState,
      action: {
        payload:
          | (Partial<Hive> & { id: string })
          | { data: Partial<Hive> & { id: string }; skipUpdateHistory?: boolean };
      },
    ) => {
      datadogRum.addAction('Modify hive', { source: 'studio-on-web' });

      // Support both old format { id, ...data } and new format { data: { id, ...data }, skipUpdateHistory }
      const isNewFormat = 'data' in action.payload;
      const data = isNewFormat
        ? (action.payload as { data: Partial<Hive> & { id: string }; skipUpdateHistory?: boolean })
            .data
        : (action.payload as Partial<Hive> & { id: string });
      const skipUpdateHistory = isNewFormat
        ? (action.payload as { data: Partial<Hive> & { id: string }; skipUpdateHistory?: boolean })
            .skipUpdateHistory ?? false
        : false;
      const id = data.id;
      const {
        modified: { hives },
      } = prevState;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          hives: {
            ...hives,
            [id]: { ...hives[id], ...data },
          },
        },
      };
    },
    addDeskOccupancySensor3pWithSpot: (
      prevState,
      action: { payload: { x: number; y: number; name?: string; floor_id: string; id?: string } },
    ) => {
      const { x, y, floor_id } = action.payload;
      const name =
        action.payload.name ??
        `Sensor3P ${prevState.base.sensors3p.length + prevState.added.sensors3p.length + 1}`;
      const spotId = generateId.spot();
      const sensor3pId = action.payload.id ?? generateId.sensor3p();
      return {
        ...updateHistory(prevState),
        selected: [sensor3pId],
        added: {
          ...prevState.added,
          spots: [
            ...prevState.added.spots,
            {
              name: `Desk ${name}`,
              id: spotId,
              spot_type: 'desk',
              location: [x, y] as [number, number],
              floor_id,
              device_type: 'desk_sensor',
            },
          ],
          sensors3p: [
            ...prevState.added.sensors3p,
            {
              id: sensor3pId,
              name,
              device_type: 'desk_sensor' as Sensor3P['device_type'],
              floor_id,
              location: [x, y] as [number, number],
              provider: 'dt',
              spot_id: spotId,
            },
          ],
        },
      };
    },
    addSpot: (prevState, action: { payload: { x: number; y: number; floor_id: string } }) => {
      const { x, y, floor_id } = action.payload;
      const {
        added: { spots },
      } = prevState;
      const spotId = generateId.spot();
      const totalSpots = prevState.base.spots.length + prevState.added.spots.length;
      return {
        ...updateHistory(prevState),
        selected: [spotId],
        added: {
          ...prevState.added,
          spots: [
            ...spots,
            {
              name: `Spot ${totalSpots + 1}`,
              id: spotId,
              spot_type: 'desk',
              location: [x, y] as [number, number],
              floor_id,
            },
          ],
        },
      };
    },
    modifySpot: (
      prevState,
      action: {
        payload: { data: Partial<Spot> & { id: string }; skipUpdateHistory?: boolean };
      },
    ) => {
      datadogRum.addAction('Modify spot', { source: 'studio-on-web' });
      const { data, skipUpdateHistory = false } = action.payload;
      const id = data.id;
      const {
        modified: { spots },
      } = prevState;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          spots: {
            ...spots,
            [id]: { ...spots[id], ...data },
          },
        },
      };
    },
    // Applies a partial change to all currently-selected spots in a single undo step.
    bulkModifySpots: (prevState, action: { payload: Partial<Spot> }) => {
      const changes = action.payload;
      return updateSelected({
        prevState: updateHistory(prevState),
        spotUpdateFunc: () => changes,
      });
    },
    setArchiveStatus: (
      prevState,
      action: { payload: { ids: string[]; status: 'ARCHIVED' | 'ACTIVE' } },
    ) => {
      const { ids, status } = action.payload;
      const archiveChanges = { ...prevState.archiveChanges };
      for (const id of ids) {
        const baseSensor = prevState.base.sensors.find((s) => s.id === id);
        const baseHive = prevState.base.hives.find((h) => h.id === id);
        const baseStatus = baseSensor?.deviceStatus ?? baseHive?.deviceStatus ?? 'ACTIVE';
        if (baseStatus === status) {
          delete archiveChanges[id];
        } else {
          archiveChanges[id] = status;
        }
      }
      return { ...updateHistory(prevState), archiveChanges };
    },
    addSensor3p: (
      prevState,
      action: {
        payload: {
          x: number;
          y: number;
          floor_id: string;
          device_type: Sensor3P['device_type'];
          id?: string;
          name?: string;
        };
      },
    ) => {
      datadogRum.addAction('Add sensor3p', { source: 'studio-on-web' });
      const { x, y, floor_id, device_type } = action.payload;
      const sensor3pId = action.payload.id ?? generateId.sensor3p();
      const totalSensors3p = prevState.base.sensors3p.length + prevState.added.sensors3p.length;
      const name = action.payload.name ?? `Sensor3P ${totalSensors3p + 1}`;
      return {
        ...updateHistory(prevState),
        selected: [sensor3pId],
        added: {
          ...prevState.added,
          sensors3p: [
            ...prevState.added.sensors3p,
            {
              id: sensor3pId,
              name,
              provider: 'dt',
              device_type,
              floor_id,
              location: [x, y] as [number, number],
            },
          ],
        },
      };
    },

    modifySensor3p: (
      prevState,
      action: {
        payload: { data: Partial<Sensor3P> & { id: string }; skipUpdateHistory?: boolean };
      },
    ) => {
      datadogRum.addAction('Modify sensor3p', { source: 'studio-on-web' });
      const { data, skipUpdateHistory = false } = action.payload;
      const id = data.id;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          sensors3p: {
            ...prevState.modified.sensors3p,
            [id]: { ...prevState.modified.sensors3p[id], ...data },
          },
        },
      };
    },

    // Applies a partial change to all currently-selected sensor3p items in a single undo step.
    bulkModifySensors3p: (prevState, action: { payload: Partial<Sensor3P> }) => {
      const changes = action.payload;
      return updateSelected({
        prevState: updateHistory(prevState),
        sensor3pUpdateFunc: () => changes,
      });
    },

    setSensors3p: (prevState, action: { payload: { sensors3p: Sensor3P[] } }) => {
      return {
        ...prevState,
        base: { ...prevState.base, sensors3p: action.payload.sensors3p },
        initialized: true,
      };
    },

    addGateway3p: (prevState, action: { payload: { x: number; y: number; floor_id: string } }) => {
      datadogRum.addAction('Add gateway3p', { source: 'studio-on-web' });
      const { x, y, floor_id } = action.payload;
      const gateway3pId = generateId.gateway3p();
      const totalGateways3p = prevState.base.gateways3p.length + prevState.added.gateways3p.length;
      return {
        ...updateHistory(prevState),
        selected: [gateway3pId],
        added: {
          ...prevState.added,
          gateways3p: [
            ...prevState.added.gateways3p,
            {
              id: gateway3pId,
              name: `Gateway3P ${totalGateways3p + 1}`,
              provider: 'dt',
              floor_id,
              location: [x, y] as [number, number],
            },
          ],
        },
      };
    },

    modifyGateway3p: (
      prevState,
      action: {
        payload: { data: Partial<Gateway3P> & { id: string }; skipUpdateHistory?: boolean };
      },
    ) => {
      datadogRum.addAction('Modify gateway3p', { source: 'studio-on-web' });
      const { data, skipUpdateHistory = false } = action.payload;
      const id = data.id;
      return {
        ...(skipUpdateHistory ? prevState : updateHistory(prevState)),
        modified: {
          ...prevState.modified,
          gateways3p: {
            ...prevState.modified.gateways3p,
            [id]: { ...prevState.modified.gateways3p[id], ...data },
          },
        },
      };
    },

    // Applies a partial change to all currently-selected gateway3p items in a single undo step.
    bulkModifyGateways3p: (prevState, action: { payload: Partial<Gateway3P> }) => {
      const changes = action.payload;
      return updateSelected({
        prevState: updateHistory(prevState),
        gateway3pUpdateFunc: () => changes,
      });
    },

    setGateways3p: (prevState, action: { payload: { gateways3p: Gateway3P[] } }) => {
      return {
        ...prevState,
        base: { ...prevState.base, gateways3p: action.payload.gateways3p },
        initialized: true,
      };
    },

    delete: (prevState, action: { payload: { ids: string[] } }) => {
      const { ids: deleteIds } = action.payload;
      datadogRum.addAction('Delete items', {
        source: 'studio-on-web',
        count: deleteIds.length,
      });

      const { added, deleted, modified } = prevState;

      // When a desk sensor is deleted, cascade-delete its co-created spot so it
      // doesn't remain as an orphaned pin on the canvas.
      const linkedSpotIds = deleteIds.flatMap((id) => {
        const sensor = [...prevState.base.sensors, ...added.sensors].find((s) => s.id === id);
        return sensor?.spot_id ? [sensor.spot_id] : [];
      });
      const effectiveDeleteIds = [...new Set([...deleteIds, ...linkedSpotIds])];

      const selected = new Set<string>(prevState.selected);
      const archiveChanges = { ...prevState.archiveChanges };
      effectiveDeleteIds.forEach((id) => {
        selected.delete(id);
        delete archiveChanges[id];
      });

      return {
        ...updateHistory(prevState),
        selected: Array.from(selected),
        modified: {
          // TODO: we should enforce that "id" is present on all resources in our typings
          floorplans: pickBy(
            modified.floorplans,
            (r) => !r.id || !effectiveDeleteIds.includes(r.id),
          ),
          sensors: pickBy(modified.sensors, (r) => !r.id || !effectiveDeleteIds.includes(r.id)),
          rooms: pickBy(modified.rooms, (r) => !r.id || !effectiveDeleteIds.includes(r.id)),
          zones: pickBy(modified.zones, (r) => !r.id || !effectiveDeleteIds.includes(r.id)),
          hives: pickBy(modified.hives, (r) => !r.id || !effectiveDeleteIds.includes(r.id)),
          spots: pickBy(modified.spots, (r) => !r.id || !effectiveDeleteIds.includes(r.id)),
          sensors3p: pickBy(modified.sensors3p, (r) => !r.id || !effectiveDeleteIds.includes(r.id)),
          gateways3p: pickBy(
            modified.gateways3p,
            (r) => !r.id || !effectiveDeleteIds.includes(r.id),
          ),
        },
        added: {
          sensors: added.sensors.filter((r) => !effectiveDeleteIds.includes(r.id)),
          rooms: added.rooms.filter((r) => !effectiveDeleteIds.includes(r.id)),
          zones: added.zones.filter((r) => !effectiveDeleteIds.includes(r.id)),
          hives: added.hives.filter((r) => !effectiveDeleteIds.includes(r.id)),
          floorplans: added.floorplans.filter((r) => !effectiveDeleteIds.includes(r.id)),
          spots: added.spots.filter((r) => !effectiveDeleteIds.includes(r.id)),
          sensors3p: added.sensors3p.filter((r) => !effectiveDeleteIds.includes(r.id)),
          gateways3p: added.gateways3p.filter((r) => !effectiveDeleteIds.includes(r.id)),
        },
        archiveChanges,
        deleted: [...deleted, ...effectiveDeleteIds],
      };
    },
    undo: (prevState) => {
      const { undoState } = prevState;
      if (undoState.length === 0) {
        return prevState;
      }
      const lastState = undoState[undoState.length - 1];
      return {
        ...lastState,
        // preserve current base state (API data)
        base: prevState.base,
        undoState: undoState.slice(0, undoState.length - 1),
        redoState: [...prevState.redoState, prevState].slice(-historyCapacity),
      };
    },
    redo: (prevState) => {
      const { redoState } = prevState;
      if (redoState.length === 0) {
        return prevState;
      }
      const lastState = redoState[redoState.length - 1];
      return {
        ...lastState,
        // preserve current base state (API data)
        base: prevState.base,
        redoState: redoState.slice(0, redoState.length - 1),
        undoState: [...prevState.undoState, prevState].slice(-historyCapacity),
      };
    },
    updateError: (prevState, action: { payload: { macAddress: string[]; message: string } }) => {
      const { macAddress: errorIds, message } = action.payload;
      const newErrors = { ...prevState.errors };
      errorIds.forEach((id) => {
        newErrors[id] = message;
      });
      return {
        ...prevState,
        errors: newErrors,
      };
    },
    removeError: (prevState, action: { payload: { macAddress: string[] } }) => {
      const { macAddress: errorIds } = action.payload;
      const newErrors = { ...prevState.errors };
      errorIds.forEach((id) => {
        delete newErrors[id];
      });
      return {
        ...prevState,
        errors: newErrors,
      };
    },
    set: (
      prevState,
      action: {
        payload: Omit<CoreReducerState['base'], 'spots' | 'sensors3p' | 'gateways3p'>;
      },
    ) => {
      const { sensors, rooms, zones, floorplans, hives, unplotted } = action.payload;
      return {
        ...prevState,
        base: {
          sensors,
          rooms,
          zones,
          hives,
          unplotted,
          floorplans,
          spots: prevState.base.spots,
          sensors3p: prevState.base.sensors3p,
          gateways3p: prevState.base.gateways3p,
        },
        initialized: true,
      };
    },
    clearEditState: (prevState) => {
      const { modified, deleted, added, archiveChanges } = getEmptyState();
      return { ...prevState, modified, deleted, added, archiveChanges };
    },
    update: (
      prevState,
      action: {
        payload: SaveResult;
      },
    ) => {
      const { base, modified, added } = prevState;
      const {
        updateSensors,
        upsertSensors,
        updateRooms,
        updateZones,
        updateHives,
        createHives,
        updateFloorplans,
        deleteSensors,
        deleteFloorplans,
        deleteHives,
        deleteRooms,
        deleteZones,
        createRooms,
        createZones,
        mirrorSensors = [],
        cloneSensors = [],
        createSpots,
        updateSpots,
        deleteSpots,
        createSensors3p,
        updateSensors3p,
        deleteSensors3p,
        createGateways3p,
        updateGateways3p,
        deleteGateways3p,
      } = action.payload;

      let addedSensors = [...added.sensors];
      let addedRooms = [...added.rooms];
      let addedZones = [...added.zones];
      let addedHives = [...added.hives];
      let addedSpots = [...added.spots];
      let addedSensors3p = [...added.sensors3p];
      let addedGateways3p = [...added.gateways3p];
      const modifiedSensors = { ...modified.sensors };
      const modifiedRooms = { ...modified.rooms };
      const modifiedZones = { ...modified.zones };
      const modifiedHives = { ...modified.hives };
      const modifiedSpots = { ...modified.spots };
      const modifiedSensors3p = { ...modified.sensors3p };
      const modifiedGateways3p = { ...modified.gateways3p };
      deleteSensors.forEach(({ id }) => {
        delete modifiedSensors[id];
        addedSensors = addedSensors.filter(({ id: _id }) => id !== _id);
      });
      updateSensors.forEach(({ macAddress }) => {
        const foundId = Object.values(modifiedSensors).find(
          (sensor) => sensor.macAddress === macAddress,
        )?.id;
        if (foundId) {
          delete modifiedSensors[foundId];
        }
        addedSensors = addedSensors.filter(({ id: _id }) => foundId !== _id);
      });
      [...updateRooms, ...createRooms, ...deleteRooms].forEach(({ id }) => {
        delete modifiedRooms[id];
        addedRooms = addedRooms.filter(({ id: _id }) => id !== _id);
      });
      [...updateZones, ...createZones, ...deleteZones].forEach(({ id }) => {
        delete modifiedZones[id];
        addedZones = addedZones.filter(({ id: _id }) => id !== _id);
      });
      [...updateHives, ...deleteHives].forEach(({ id }) => {
        delete modifiedHives[id];
        addedHives = addedHives.filter(({ id: _id }) => id !== _id);
      });
      // Created sensors get server-generated IDs that don't match client temp IDs.
      // Clear only non-virtual added sensors — virtual sensors are handled below.
      if (upsertSensors.length > 0) {
        addedSensors
          .filter((s) => !s.isMirror && !s.isClone)
          .forEach(({ id }) => delete modifiedSensors[id]);
        addedSensors = addedSensors.filter((s) => s.isMirror || s.isClone);
      }

      // Virtual sensors are saved via separate mutations (clone and mirror independently).
      // Only clear added sensors of the type(s) that were actually saved — saving
      // mirrors should not discard unsaved clones, and vice versa.
      const savedMirrors = mirrorSensors.length > 0;
      const savedClones = cloneSensors.length > 0;
      const savedVirtualSensors = [...mirrorSensors, ...cloneSensors];
      if (savedVirtualSensors.length > 0) {
        const virtualToRemove = new Set(
          addedSensors
            .filter((s) => (s.isMirror && savedMirrors) || (s.isClone && savedClones))
            .map((s) => s.id),
        );
        virtualToRemove.forEach((id) => delete modifiedSensors[id]);
        addedSensors = addedSensors.filter((s) => !virtualToRemove.has(s.id));
      }

      // Same for hives.
      if (createHives.length > 0) {
        addedHives.forEach(({ id }) => delete modifiedHives[id]);
        addedHives = [];
      }

      // Same for spots: server assigns IDs, so clear all added spots on creation.
      [...updateSpots, ...deleteSpots].forEach(({ id }) => {
        delete modifiedSpots[id];
        addedSpots = addedSpots.filter(({ id: _id }) => id !== _id);
      });
      if (createSpots.length > 0) {
        addedSpots.forEach(({ id }) => delete modifiedSpots[id]);
        addedSpots = [];
      }

      // Same for sensor3p: server assigns IDs on creation.
      [...updateSensors3p, ...deleteSensors3p].forEach(({ id }) => {
        delete modifiedSensors3p[id];
        addedSensors3p = addedSensors3p.filter(({ id: _id }) => id !== _id);
      });
      if (createSensors3p.length > 0) {
        addedSensors3p.forEach(({ id }) => delete modifiedSensors3p[id]);
        addedSensors3p = [];
      }

      // Same for gateway3p: server assigns IDs on creation.
      [...updateGateways3p, ...deleteGateways3p].forEach(({ id }) => {
        delete modifiedGateways3p[id];
        addedGateways3p = addedGateways3p.filter(({ id: _id }) => id !== _id);
      });
      if (createGateways3p.length > 0) {
        addedGateways3p.forEach(({ id }) => delete modifiedGateways3p[id]);
        addedGateways3p = [];
      }

      // Floor plans don't support idempotent creation and also don't really have a stable id.
      // For now, we just clear out all floor plan changes.

      // Clear successfully archived/unarchived items from archiveChanges
      const archiveChanges = { ...prevState.archiveChanges };
      (action.payload.archivedIds ?? []).forEach((id) => delete archiveChanges[id]);

      return {
        ...prevState,
        modified: {
          sensors: modifiedSensors,
          rooms: modifiedRooms,
          zones: modifiedZones,
          hives: modifiedHives,
          floorplans: {},
          spots: modifiedSpots,
          sensors3p: modifiedSensors3p,
          gateways3p: modifiedGateways3p,
        },
        archiveChanges,
        deleted: [],
        added: {
          floorplans: [],
          hives: addedHives,
          rooms: addedRooms,
          sensors: addedSensors,
          zones: addedZones,
          spots: addedSpots,
          sensors3p: addedSensors3p,
          gateways3p: addedGateways3p,
        },
        base: {
          sensors: [
            ...(() => {
              const excludeIds = new Set([
                ...deleteSensors.map((u) => u.id),
                ...updateSensors.map((u) => u.id),
                ...savedVirtualSensors.map((vs) => vs.id),
              ]);
              return base.sensors.filter((s) => !excludeIds.has(s.id));
            })(),
            ...updateSensors,
            // Only add virtual sensors that have a valid sourceMacAddress
            ...savedVirtualSensors.filter((vs) => !!vs.sourceMacAddress),
          ],
          rooms: [
            ...base.rooms
              .filter((s) => !deleteRooms.some((u) => u.id === s.id))
              .filter((s) => !updateRooms.some((u) => u.id === s.id)),
            ...createRooms.map((r) => ({ ...r, sensors: [] })),
            ...updateRooms,
          ],
          zones: [
            ...base.zones
              .filter((s) => !deleteZones.some((u) => u.id === s.id))
              .filter((s) => !updateZones.some((u) => u.id === s.id)),
            ...createZones,
            ...updateZones,
          ],
          hives: [
            ...base.hives
              .filter((s) => !deleteHives.some((u) => u.id === s.id))
              .filter((s) => !updateHives.some((u) => u.id === s.id)),
            ...updateHives,
          ],
          unplotted: [
            ...base.unplotted
              .filter((s) => !deleteHives.some((u) => u.id === s.id))
              .filter((s) => !updateHives.some((u) => u.id === s.id)),
          ],
          floorplans: [
            ...base.floorplans
              .filter((s) => !deleteFloorplans.some((u) => u.id === s.id))
              .filter((s) => !updateFloorplans.some((u) => u.id === s.id)),
            ...updateFloorplans.map(({ coordinates, ...rest }) => {
              return { ...rest, coordinates, rotation: getRectRotation(coordinates) };
            }),
          ],
          spots: [
            ...base.spots
              .filter((s) => !deleteSpots.some((u) => u.id === s.id))
              .filter((s) => !updateSpots.some((u) => u.id === s.id)),
            ...createSpots,
            ...updateSpots,
          ],
          // Sensor3p/gateway3p base is refreshed via refetchSensors3pGateways3p after save.
          // We still reconcile known deletes/updates here for immediate UI consistency.
          sensors3p: [
            ...base.sensors3p
              .filter((s) => !deleteSensors3p.some((u) => u.id === s.id))
              .filter((s) => !updateSensors3p.some((u) => u.id === s.id)),
            ...createSensors3p,
            ...updateSensors3p,
          ],
          gateways3p: [
            ...base.gateways3p
              .filter((g) => !deleteGateways3p.some((u) => u.id === g.id))
              .filter((g) => !updateGateways3p.some((u) => u.id === g.id)),
            ...createGateways3p,
            ...updateGateways3p,
          ],
        },
        initialized: true,
      };
    },

    setSpots: (prevState, action: { payload: { spots: Spot[] } }) => {
      return {
        ...prevState,
        base: { ...prevState.base, spots: action.payload.spots },
        initialized: true,
      };
    },

    reset: (prev) => {
      // special case: preserve some settings
      const highlightRelatedDevices = prev.highlightRelatedDevices;
      const heatmapStyle = prev.heatmapStyle;
      const newState = getEmptyState();
      newState.highlightRelatedDevices = highlightRelatedDevices;
      newState.heatmapStyle = heatmapStyle;
      return newState;
    },

    setHeatmapStyle: (prevState, action: { payload: HeatmapStyle }) => {
      return { ...prevState, heatmapStyle: action.payload };
    },

    setLayerState: (
      prevState,
      action: { payload: { layer: LayerName; state: Partial<LayerState> } },
    ) => {
      const { layer, state } = action.payload;

      const newSelected = new Set(prevState.selected);
      if (layer === 'floorplans' && state.locked) {
        // when floorplans are locked, we must deselect all floorplans
        [...prevState.base.floorplans, ...prevState.added.floorplans]
          .map((floorplan) => floorplan.id)
          .forEach(newSelected.delete.bind(newSelected));
      }

      const newLayers = {
        ...prevState.layers,
        [layer]: { ...prevState.layers[layer], ...state },
      };
      saveLayers(newLayers);
      return {
        ...prevState,
        selected: Array.from(newSelected),
        layers: newLayers,
      };
    },
    toggleSubLayer: (
      prevState,
      action: { payload: { layer: LayerName; subLayer: string; toggle: boolean } },
    ) => {
      const { layer, subLayer, toggle } = action.payload;
      let subLayers = prevState.layers[layer].subLayers ?? [];
      if (toggle) {
        subLayers = [...subLayers, subLayer].sort();
      } else {
        subLayers = subLayers.filter((m) => m !== subLayer);
      }
      const newLayers = {
        ...prevState.layers,
        [layer]: { ...prevState.layers[layer], subLayers },
      };
      saveLayers(newLayers);
      return {
        ...prevState,
        layers: newLayers,
      };
    },

    setSearchText: (prevState, action: { payload: string }) => {
      return {
        ...prevState,
        search: { ...prevState.search, text: action.payload },
      };
    },

    setSearchHoverId: (prevState, action: { payload: string | null }) => {
      return {
        ...prevState,
        search: { ...prevState.search, hoverItemId: action.payload },
      };
    },

    toggleHighlightRelatedDevices: (prevState) => {
      return {
        ...prevState,
        highlightRelatedDevices: !prevState.highlightRelatedDevices,
      };
    },

    setSelectedHovered: (
      prevState,
      action: {
        payload: {
          hover: boolean;
        };
      },
    ) => {
      return {
        ...prevState,
        selectedHovered: action.payload.hover,
      };
    },

    copy: (prevState) => {
      const { selected, base, added, modified } = prevState;
      const selectedRooms = resolveSelectedItems(selected, base.rooms, added.rooms, modified.rooms);
      const selectedZones = resolveSelectedItems(selected, base.zones, added.zones, modified.zones);
      const selectedSpots = resolveSelectedItems(selected, base.spots, added.spots, modified.spots);
      // Exclude cloned/mirrored sensors from copy
      const selectedSensors = resolveSelectedItems(
        selected,
        base.sensors,
        added.sensors,
        modified.sensors,
        (s) => !s.isClone && !s.isMirror,
      );

      if (
        !selectedRooms.length &&
        !selectedZones.length &&
        !selectedSensors.length &&
        !selectedSpots.length
      ) {
        return prevState;
      }

      return {
        ...prevState,
        clipboard: {
          rooms: selectedRooms,
          zones: selectedZones,
          sensors: selectedSensors,
          spots: selectedSpots,
          origin: 'copy' as const,
        },
      };
    },

    // Clone directly creates new sensors at an offset from the originals.
    // Cloned/mirrored sensors and sensors without MAC addresses are excluded.
    clone: (
      prevState,
      action: { payload: { savedCloneMacs: string[]; savedMirrorMacs: string[] } },
    ) => {
      const { selected, base, added, modified } = prevState;
      const { savedCloneMacs } = action.payload;

      const selectedSensors = resolveSelectedItems(
        selected,
        base.sensors,
        added.sensors,
        modified.sensors,
        (s) => !s.isClone && !s.isMirror && !s.isArchived && !!s.macAddress,
      );

      if (selectedSensors.length === 0) {
        return prevState;
      }

      const OFFSET = 2;
      const newSensors = selectedSensors.map((sensor) => {
        const nextIndex = getNextVirtualSeq(
          savedCloneMacs,
          added.sensors,
          sensor.macAddress,
          'clone',
        );
        return {
          ...sensor,
          id: generateId.sensor(),
          center: [sensor.center[0] + OFFSET, sensor.center[1]] as TXYPoint,
          name: `${sensor.name}_${nextIndex}`,
          macAddress: '', // Backend generates the real MAC on save
          isClone: true,
          sourceMacAddress: sensor.macAddress,
        };
      });

      return {
        ...updateHistory(prevState),
        selected: newSensors.map((s) => s.id),
        added: {
          ...prevState.added,
          sensors: [...prevState.added.sensors, ...newSensors],
        },
      };
    },

    // Mirror directly creates new sensors at an offset from the originals.
    // Only supports traffic mode sensors. Cloned/mirrored sensors and sensors without MAC are excluded.
    mirror: (
      prevState,
      action: { payload: { savedCloneMacs: string[]; savedMirrorMacs: string[] } },
    ) => {
      const { selected, base, added, modified } = prevState;
      const { savedMirrorMacs } = action.payload;

      const selectedSensors = resolveSelectedItems(
        selected,
        base.sensors,
        added.sensors,
        modified.sensors,
        (s) =>
          s.mode === ESensorMode.TRAFFIC &&
          !s.isClone &&
          !s.isMirror &&
          !s.isArchived &&
          !!s.macAddress,
      );

      if (selectedSensors.length === 0) {
        return prevState;
      }

      const OFFSET = 2;
      const newSensors = selectedSensors.map((sensor) => {
        const nextIndex = getNextVirtualSeq(
          savedMirrorMacs,
          added.sensors,
          sensor.macAddress,
          'mirror',
        );
        return {
          ...sensor,
          id: generateId.sensor(),
          center: [sensor.center[0] + OFFSET, sensor.center[1]] as TXYPoint,
          name: `${sensor.name}_${nextIndex}`,
          macAddress: '', // Backend generates the real MAC on save
          inDirection:
            sensor.inDirection !== undefined
              ? ((sensor.inDirection === 0 ? 1 : 0) as TInDirection)
              : undefined,
          connectionHealth: { status: 'NOT_CONNECTED' as const, codes: [] },
          isMirror: true,
          sourceMacAddress: sensor.macAddress,
        };
      });

      return {
        ...updateHistory(prevState),
        selected: newSensors.map((s) => s.id),
        added: {
          ...prevState.added,
          sensors: [...prevState.added.sensors, ...newSensors],
        },
      };
    },

    // Duplicate = copy + paste in place, then restore the original clipboard.
    // Reuses copy and paste logic so behavior stays consistent.
    duplicate: (prevState): StudioReducerState => {
      const savedClipboard = prevState.clipboard;
      const stateAfterCopy = studioSlice.reducer(prevState, studioActions.copy());
      const stateAfterPaste = studioSlice.reducer(stateAfterCopy, studioActions.paste());
      return {
        ...stateAfterPaste,
        clipboard: savedClipboard,
      };
    },

    cut: (prevState): StudioReducerState => {
      const { selected } = prevState;
      // First copy the selected items
      const stateAfterCopy = studioSlice.reducer(prevState, studioActions.copy());
      // Then delete them
      const stateAfterDelete = studioSlice.reducer(
        stateAfterCopy,
        studioActions.delete({ ids: selected }),
      );
      // Mark clipboard origin as cut so paste preserves original names
      return {
        ...stateAfterDelete,
        clipboard: stateAfterDelete.clipboard
          ? { ...stateAfterDelete.clipboard, origin: 'cut' as const }
          : null,
      };
    },

    paste: (prevState, action: { payload: PasteActionPayload }): StudioReducerState => {
      const { clipboard } = prevState;
      if (!clipboard) {
        return prevState;
      }

      const allItems = [
        ...clipboard.rooms,
        ...clipboard.zones,
        ...clipboard.sensors,
        ...clipboard.spots,
      ];
      if (allItems.length === 0) {
        return prevState;
      }

      const allCoordinates = allItems.flatMap((item) => itemToCoordinates(item));
      const [sourceX, sourceY, sourceWidth, sourceHeight] = pointsToRect(...allCoordinates);
      const sourceCenterX = sourceX + sourceWidth / 2;
      const sourceCenterY = sourceY + sourceHeight / 2;

      let targetX = 0;
      let targetY = 0;
      if (action?.payload) {
        targetX = action.payload.x;
        targetY = action.payload.y;
      } else {
        // Cascade: offset from currently selected items (newly pasted items are auto-selected)
        const selectedItem = [
          ...prevState.base.rooms,
          ...prevState.added.rooms,
          ...prevState.base.zones,
          ...prevState.added.zones,
          ...prevState.base.sensors,
          ...prevState.added.sensors,
          ...prevState.base.spots,
          ...prevState.added.spots,
        ].find((item) => prevState.selected.includes(item.id));
        if (selectedItem) {
          const coords = itemToCoordinates(selectedItem);
          const [x, y, width, height] = pointsToRect(...coords);
          targetX = x + width / 2 + 2;
          targetY = y + height / 2 + 2;
        } else {
          // Fallback: offset from clipboard source center
          targetX = sourceCenterX + 2;
          targetY = sourceCenterY + 2;
        }
      }

      const offsetX = targetX - sourceCenterX;
      const offsetY = targetY - sourceCenterY;

      if (clipboard.origin === 'cut') {
        return handleCutPaste(prevState, clipboard, allItems, offsetX, offsetY);
      }

      // Copy+Paste: create new items with generated IDs and smart naming
      const collectNames = <T extends { id: string; name: string }>(
        base: T[],
        added: T[],
        modified: Record<string, Partial<T>>,
      ): string[] =>
        [...base, ...added]
          .filter((i) => !prevState.deleted.includes(i.id))
          .map((i) => modified[i.id]?.name ?? i.name)
          .filter((name): name is string => name != null);

      const allRoomNames = collectNames(
        prevState.base.rooms,
        prevState.added.rooms,
        prevState.modified.rooms,
      );
      const allZoneNames = collectNames(
        prevState.base.zones,
        prevState.added.zones,
        prevState.modified.zones,
      );
      const allSensorNames = collectNames(
        prevState.base.sensors,
        prevState.added.sensors,
        prevState.modified.sensors,
      );
      const allSpotNames = collectNames(
        prevState.base.spots,
        prevState.added.spots,
        prevState.modified.spots,
      );

      const createPastedItem = <
        T extends { coordinates: TRectCoords | TPolygonCoords; name: string },
      >(
        item: T,
        generateIdFn: () => string,
        existingNames: string[],
      ): T & { id: string } => {
        const id = generateIdFn();
        const name = generateCopyName(item.name, existingNames);
        existingNames.push(name);
        return {
          ...item,
          id,
          name,
          coordinates: item.coordinates.map(([coordX, coordY]) => [
            coordX + offsetX,
            coordY + offsetY,
          ]) as T['coordinates'],
          tags: undefined,
        };
      };

      const newRooms = clipboard.rooms.map((room) =>
        createPastedItem(room, generateId.room, allRoomNames),
      );
      const newZones = clipboard.zones.map((zone) =>
        createPastedItem(zone, generateId.zone, allZoneNames),
      );

      const newSpots = clipboard.spots.map((spot) => {
        const name = generateCopyName(spot.name, allSpotNames);
        allSpotNames.push(name);
        return {
          ...spot,
          id: generateId.spot(),
          name,
          location: [spot.location[0] + offsetX, spot.location[1] + offsetY] as [number, number],
          tags: undefined,
        };
      });

      const newSensors = clipboard.sensors.map((sensor) => {
        const id = generateId.sensor();
        const name = generateCopyName(sensor.name, allSensorNames);
        allSensorNames.push(name);
        return {
          ...sensor,
          id,
          spot_id: undefined,
          name,
          macAddress: '',
          center: [sensor.center[0] + offsetX, sensor.center[1] + offsetY] as TXYPoint,
          // Strip virtual sensor fields — copies become regular placeholders
          isMirror: undefined,
          isClone: undefined,
          sourceMacAddress: undefined,
          sourceLockedByContextMenu: undefined,
        };
      });

      return {
        ...updateHistory(prevState),
        clipboard: prevState.clipboard,
        selected: [
          ...newRooms.map((r) => r.id),
          ...newZones.map((z) => z.id),
          ...newSensors.map((s) => s.id),
          ...newSpots.map((s) => s.id),
        ],
        added: {
          ...prevState.added,
          rooms: [...prevState.added.rooms, ...newRooms],
          zones: [...prevState.added.zones, ...newZones],
          sensors: [...prevState.added.sensors, ...newSensors],
          spots: [...prevState.added.spots, ...newSpots],
        },
      };
    },
  },
});

export default studioSlice.reducer;
export const { actions: studioActions } = studioSlice;

export function useStudioState() {
  return useAppSelector((state) => state.studio);
}
